import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Row } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import MobilePageHeader from 'components/shared/MobilePageHeader';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import Tabs, { TabType } from 'components/Tabs';
import usePagination from 'hooks/usePagination';
import { usePortalName } from 'hooks/usePortalConnections';
import useWorkflowCount from 'hooks/useWorkflowCount';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import useWorkflows from 'hooks/useWorkflows';
import Workflow, { WorkflowStatus } from 'models/Workflow';
import { WorkflowsIndexParams } from 'services/api/workflows';
import {
  serializeSort,
  StatusChip,
  StatusFilterValue,
  Tab,
  useInboundReferralsActions,
  useInboundReferralsStore,
} from 'stores/inboundReferralsStore';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, H2 } from 'styles/typography';
import FilterIconAlt from 'svg/FilterIconAlt';

import { INBOUND_REFERRALS_ROUTES, REFERRAL_INTAKE_API_NAME } from './constants';
import inboundReferralsTableColumns from './inboundReferralsTableColumns';
import ReferralModal from './ReferralModal';
import { LIST_STATUS_DISPLAY, ListStatus } from './referralProgress';
import { TONE_STYLES } from './StatusBadge';

// Legend swatches — only the four surfaced states (pending/error aren't shown).
const LEGEND: ListStatus[] = ['actionNeeded', 'processing', 'readyForReview', 'complete'];

type StatusOption = { value: StatusFilterValue; label: string };
const STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'All' },
  { value: 'actionNeeded', label: 'Action Needed' },
  { value: 'processing', label: 'Olio Agent' },
  { value: 'readyForReview', label: 'Ready for Review' },
  { value: 'complete', label: 'Complete' },
];

// Matches Workflow#isTerminal — History is the terminal runs; Active is everything still in
// flight. Server-side now (status / status.not) instead of a client-side .filter().
const TERMINAL_STATUSES: string[] = [WorkflowStatus.COMPLETED, WorkflowStatus.CANCELED];

function tabParams(tab: Tab): Partial<WorkflowsIndexParams> {
  return tab === 'history' ? { status: TERMINAL_STATUSES } : { 'status.not': TERMINAL_STATUSES };
}

// The chip row (Active tab only, see below). 'actionNeeded' here is a deliberate departure from
// the Status dropdown's option of the same name: it's the per-building queue (work MY buildings
// can act on), not the workflow-derived list_status the dropdown maps to. Two different params
// behind one label — see stores/inboundReferralsStore.ts.
function chipParams(chip: StatusChip): Partial<WorkflowsIndexParams> {
  if (chip === 'actionNeeded') return { queue: true };
  if (chip === 'readyForReview') return { listStatus: 'ready_for_review' };
  return {};
}

// The Status dropdown (both tabs). Every option maps onto something the API filters on
// directly — 'processing'/'actionNeeded'/'readyForReview' via list_status (the same
// precedence-ordered pill states the row's own status pill reads), 'complete' via status.
function statusFilterParams(status: StatusFilterValue): Partial<WorkflowsIndexParams> {
  switch (status) {
    case 'actionNeeded':
      return { listStatus: 'action_needed' };
    case 'processing':
      return { listStatus: 'processing' };
    case 'readyForReview':
      return { listStatus: 'ready_for_review' };
    case 'complete':
      return { status: WorkflowStatus.COMPLETED };
    default:
      return {};
  }
}

export default function InboundReferralsListPage() {
  const navigate = useNavigate();
  const pagination = usePagination();
  const { canCreate } = useWorkflowPermissions();
  const [creating, setCreating] = useState(false);
  const [searchInput, setSearchInput] = useState(''); // immediate input value

  const tab = useInboundReferralsStore((state) => state.tab);
  const statusChip = useInboundReferralsStore((state) => state.statusChip);
  const filters = useInboundReferralsStore((state) => state.filters);
  const sort = useInboundReferralsStore((state) => state.sort);
  const search = useInboundReferralsStore((state) => state.search); // committed (debounced) value
  const { setTab, setStatusChip, setFilters, setSort, setSearch } = useInboundReferralsActions();

  const statusFilter = filters.status ?? 'all';

  // The chip row and the Status dropdown overlap (both can express Ready for Review, and both
  // say "Action Needed" while meaning different params), and only one of them can look chosen.
  // So choosing in one clears the other — otherwise the dropdown quietly won the query while
  // the chip still rendered as active.
  const chooseChip = (chip: StatusChip) => {
    setStatusChip(chip);
    if (chip !== 'all') setFilters((prev) => ({ ...prev, status: 'all' }));
  };

  const chooseStatusFilter = (value: StatusFilterValue) => {
    setFilters((prev) => ({ ...prev, status: value }));
    if (value !== 'all') setStatusChip('all');
  };

  const portalName = usePortalName();
  const columns = useMemo(() => inboundReferralsTableColumns(portalName), [portalName]);

  // Debounced commit (~300ms), mirroring DenialListPage/useWorkflows' nudge — the query only
  // sees the settled term, not every keystroke.
  const applySearch = useMemo(() => debounce((value: string) => setSearch(value), 300), [setSearch]);
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  useEffect(() => () => setSearch(''), [setSearch]);

  // Reset to the first page whenever a filter/search/tab changes the result set.
  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, tab, statusChip, statusFilter]);

  const queryParams: WorkflowsIndexParams = useMemo(
    () => ({
      apiName: REFERRAL_INTAKE_API_NAME,
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      ...(search ? { search } : {}),
      ...tabParams(tab),
      // Only on Active, because that's the only tab the chip row renders on — otherwise a chip
      // chosen on Active keeps filtering History with nothing on screen to say so or undo it.
      // The choice survives in the store, so coming back to Active restores it.
      ...(tab === 'active' ? chipParams(statusChip) : {}),
      ...statusFilterParams(statusFilter),
    }),
    [pagination.paging, sort, search, tab, statusChip, statusFilter]
  );

  const { workflows, meta, isLoading, isError } = useWorkflows(queryParams);

  // Chip badges (Active tab only): each is its own page_size:1 count query rather than a
  // client-side tally, because pagination means the fetched page is never the full active-tab
  // set to tally over. The Active tab label's count doubles as the "All" chip's count (same
  // params — active, no chip filter), so there's nothing extra to fetch for that one.
  const activeCount = useWorkflowCount({ apiName: REFERRAL_INTAKE_API_NAME, ...tabParams('active') });
  const actionNeededCount = useWorkflowCount(
    { apiName: REFERRAL_INTAKE_API_NAME, ...tabParams('active'), ...chipParams('actionNeeded') },
    tab === 'active'
  );
  const readyForReviewCount = useWorkflowCount(
    { apiName: REFERRAL_INTAKE_API_NAME, ...tabParams('active'), ...chipParams('readyForReview') },
    tab === 'active'
  );

  // Manual entry is a create, so a reader without the grant does not get the button (OLIO-2615).
  const createButton = canCreate ? (
    <CreateButton variant='outline-primary' onClick={() => setCreating(true)} data-testid='new-referral'>
      Create New Referral
    </CreateButton>
  ) : null;

  const tabOptions: TabType[] = [
    { label: `Active Referrals (${activeCount})`, value: 'active', 'data-cy': 'tab-active' },
    { label: 'History', value: 'history', 'data-cy': 'tab-history' },
  ];
  const tabsRow = (
    <Tabs
      tabs={tabOptions}
      selectedTab={tabOptions.find((t) => t.value === tab)}
      onTabClick={(t) => setTab(t.value as Tab)}
    />
  );

  return (
    <Page>
      {creating && <ReferralModal onClose={() => setCreating(false)} />}
      <DesktopOnly>
        <Page.Header>
          <HeaderInner>
            <TitleRow>
              <LeftControls>
                <Title>Inbound Referrals</Title>
                <SearchBox data-testid='search-patient'>
                  <Search value={searchInput} onChange={handleSearchChange} placeholder='Search Patient' />
                </SearchBox>
                <StatusSelect data-testid='status-filter'>
                  <Select<StatusOption>
                    inputId='status-filter'
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((option) => option.value === statusFilter) ?? STATUS_OPTIONS[0]}
                    onChange={(option) => chooseStatusFilter(option?.value ?? 'all')}
                    getOptionValue={(option) => option.value}
                    getOptionLabel={(option) => option.label}
                    formatOptionLabel={(option, formatMeta) =>
                      formatMeta?.context === 'value' ? `Status: ${option.label}` : option.label
                    }
                    isSearchable={false}
                  />
                </StatusSelect>
                <FiltersButton variant='ghost' size='small' data-testid='filters-button'>
                  <FilterIconAlt fill={colors.blue100} />
                  <span>Filters</span>
                </FiltersButton>
              </LeftControls>
              {createButton}
            </TitleRow>

            {tabsRow}
          </HeaderInner>
        </Page.Header>
      </DesktopOnly>
      <MobileOnly>
        <MobilePageHeader
          title='Inbound Referrals'
          actionElement={null}
          search={searchInput}
          setSearch={handleSearchChange}
          placeholder='Search Patient'>
          <MobileTabsWrapper>{tabsRow}</MobileTabsWrapper>
        </MobilePageHeader>
      </MobileOnly>
      <Page.Content>
        {tab === 'active' && (
          <Controls>
            <Chips>
              <ChipButton
                type='button'
                $active={statusChip === 'all'}
                onClick={() => chooseChip('all')}
                data-active={statusChip === 'all'}
                data-testid='chip-all'>
                All ({activeCount})
              </ChipButton>
              <ChipButton
                type='button'
                $active={statusChip === 'actionNeeded'}
                onClick={() => chooseChip('actionNeeded')}
                data-active={statusChip === 'actionNeeded'}
                data-testid='chip-action-needed'>
                Action Needed ({actionNeededCount})
              </ChipButton>
              <ChipButton
                type='button'
                $active={statusChip === 'readyForReview'}
                onClick={() => chooseChip('readyForReview')}
                data-active={statusChip === 'readyForReview'}
                data-testid='chip-ready'>
                Ready for Review ({readyForReviewCount})
              </ChipButton>
            </Chips>
            <Legend>
              {LEGEND.map((status) => (
                <LegendItem key={status}>
                  <Swatch $color={TONE_STYLES[LIST_STATUS_DISPLAY[status].tone].accent} />
                  {LIST_STATUS_DISPLAY[status].legendLabel ?? LIST_STATUS_DISPLAY[status].label}
                </LegendItem>
              ))}
            </Legend>
          </Controls>
        )}

        {isError ? (
          <Body>We couldn’t load referrals.</Body>
        ) : (
          <ControlledTable<Workflow>
            data={workflows}
            columns={columns}
            loading={isLoading}
            pageCount={meta?.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            onRowClick={(row: Row<Workflow>) => navigate(`${INBOUND_REFERRALS_ROUTES.BASE}/${row.original.id}`)}
          />
        )}
      </Page.Content>
    </Page>
  );
}

const HeaderInner = styled.div`
  padding: 24px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const Title = styled(H2)`
  white-space: nowrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 180px;
  max-width: 300px;
`;

const StatusSelect = styled.div`
  width: 180px;
  flex-shrink: 0;
`;

// The shared filters affordance (cf. FilterBar on /patients): a borderless blue
// icon + "Filters" text, not a boxed button.
const FiltersButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-weight: normal;
  color: ${colors.blue100};
  white-space: nowrap;
`;

// Match the 38px height of the shared Search pill so the two controls are the same height.
const CreateButton = styled(Button)`
  height: 38px;
  white-space: nowrap;
`;

// On mobile the tab strip lives in MobilePageHeader's centered column; stretch it full width
// and pull it flush to the header's bottom edge (cf. InsightsTabsFilterBar's TabsWrapper).
const MobileTabsWrapper = styled.div`
  width: 100%;
  margin-bottom: -24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
`;

const ChipButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1px solid ${({ $active }) => ($active ? colors.blue100 : colors.black10)};
  background: ${({ $active }) => ($active ? colors.blue10 : colors.white)};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black75)};
  font-weight: 500;
  cursor: pointer;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
`;

const LegendItem = styled(BodySmall)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.black50};
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;
